import type {
  MfaSmsEnrollmentMembers,
  ScreenMembersOnMfaSmsEnrollment,
} from '@auth0/auth0-acul-react/types';
import {
  useMfaSmsEnrollment,
  useScreen,
  useTransaction,
} from '@auth0/auth0-acul-react/mfa-sms-enrollment';
import { useMemo } from 'react';

import { useTransactionLocale } from '@/hooks/useTranslationLocale';

export const useMfaSmsEnrollmentManager = () => {
  const mfaSmsEnrollment: MfaSmsEnrollmentMembers = useMfaSmsEnrollment();
  const screen: ScreenMembersOnMfaSmsEnrollment = useScreen();
  const transaction = useTransaction();
  const locale = useTransactionLocale();

  const { texts, data, links } = screen ?? {};
  const { countryCode, countryPrefix } = transaction ?? {};

  // Get the country name translated based on the transaction locale
  const countryName = useMemo(() => {
    if (!countryCode) return undefined;
    try {
      const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
      return displayNames.of(countryCode);
    } catch {
      return undefined;
    }
  }, [countryCode, locale]);

  const handleContinueEnrollment = async (phone: string): Promise<void> => {
    await mfaSmsEnrollment.continueEnrollment({
      phone: phone?.trim() || '',
    });
  };

  const handlePickCountryCode = async (): Promise<void> => {
    await mfaSmsEnrollment.pickCountryCode({});
  };

  const handleTryAnotherMethod = async (): Promise<void> => {
    await mfaSmsEnrollment.tryAnotherMethod({});
  };

  return {
    mfaSmsEnrollment,
    handleContinueEnrollment,
    handlePickCountryCode,
    handleTryAnotherMethod,
    texts: (texts || {}) as ScreenMembersOnMfaSmsEnrollment['texts'],
    data,
    links,
    countryCode,
    countryPrefix,
    countryName,
  };
};
